import { Component } from '@theme/component';
import { CartItemsComponent } from '@theme/component-cart-items';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

// Nuffies: FREETEES = a free box of The Local with any towel (Buy X get Y code). Shopify only applies it once the
// box is in the cart, so when the shopper enters FREETEES here this code puts the box in first (if there's a towel
// and no box yet), then applies the code as normal, so the box shows straight away at $0. Without a towel the
// code is kept on the cart with a note, and assets/nuffies-motion.js adds the box as soon as a towel goes in.
// Entering FREETEES again (in any letter case) when it's already on the cart re-runs the check.
const FREE_TEES_CODE = 'FREETEES';
const FREE_TEES_HANDLE = 'the-local-20-bamboo-golf-tees-70mm';
const FREE_TEES_KEY = 'nf-freetees-added';

/**
 * Puts one box of The Local in the cart when there's a towel and no box yet.
 * @param {AbortSignal} signal
 * @returns {Promise<{ state: 'added' | 'ready' | 'needs-towel' | 'unavailable' | 'failed', variantId?: number }>}
 */
const addFreeTeesIfTowel = async (signal) => {
  const root = window.Shopify?.routes?.root || '/';
  try {
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal }).then((r) =>
      r.json()
    );
    const items = Array.isArray(cart.items) ? cart.items : [];
    const hasTowel = items.some((item) => item.product_type === 'Golf Towel');
    const hasTees = items.some((item) => item.handle === FREE_TEES_HANDLE);
    if (!hasTowel) {
      // Let assets/nuffies-motion.js add the box once a towel goes in, even if it already did once this visit.
      try {
        window.sessionStorage.removeItem(FREE_TEES_KEY);
      } catch (storageError) {
        // No storage: nothing to clear.
      }
      return { state: 'needs-towel' };
    }
    if (hasTees) return { state: 'ready' };

    const product = await fetch(`${root}products/${FREE_TEES_HANDLE}.js`, {
      headers: { Accept: 'application/json' },
      signal,
    }).then((r) => r.json());
    const variant = (product.variants || []).find((v) => v.available);
    if (!variant) return { state: 'unavailable' };

    const added = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      signal,
    });
    if (!added.ok) return { state: 'unavailable' };

    try {
      window.sessionStorage.setItem(FREE_TEES_KEY, '1');
    } catch (storageError) {
      // No storage: nothing else depends on it here.
    }
    return { state: 'added', variantId: variant.id };
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return { state: 'failed' };
  }
};

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #pending = false;

  #needsRefresh = false;

  /**
   * Keeps discount controls focusable while the pending guard prevents another mutation.
   * @returns {() => void} Restores the controls' previous states.
   */
  #disableControls() {
    const previousBusy = this.getAttribute('aria-busy');
    const input = this.querySelector('input[name="discount"]');
    const previousReadOnly = input instanceof HTMLInputElement && input.readOnly;
    const buttons = Array.from(this.querySelectorAll('button'), (button) => ({
      button,
      previousDisabled: button.getAttribute('aria-disabled'),
    }));

    this.setAttribute('aria-busy', 'true');
    if (input instanceof HTMLInputElement) input.readOnly = true;
    for (const { button } of buttons) button.setAttribute('aria-disabled', 'true');

    return () => {
      if (previousBusy === null) this.removeAttribute('aria-busy');
      else this.setAttribute('aria-busy', previousBusy);

      if (input instanceof HTMLInputElement) input.readOnly = previousReadOnly;
      for (const { button, previousDisabled } of buttons) {
        if (previousDisabled === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', previousDisabled);
      }
    };
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * Cancelled renders can resolve without validation, so retry until this refresh reaches the DOM.
   * @param {(html: string) => boolean} [shouldRender] - Validates the rendered discounts before morphing.
   */
  async #refreshDiscountSection(shouldRender) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#needsRefresh = true;
    let validated = false;

    try {
      while (!validated && this.isConnected) {
        let pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
        while (pendingQuantityUpdates.length) {
          await Promise.all(pendingQuantityUpdates);
          pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
        }
        if (!this.isConnected) return;

        await sectionRenderer.renderSection(sectionId, {
          cache: false,
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
          shouldRender: (html) => {
            validated = true;
            return shouldRender?.(html) ?? true;
          },
        });
      }

      if (validated) this.#needsRefresh = false;
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart discounts',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /**
   * Replaces stale pills before accepting another action against the refreshed controls.
   */
  async #refreshStaleDiscounts() {
    this.#pending = true;
    const restoreControls = this.#disableControls();

    try {
      await this.#refreshDiscountSection();
    } finally {
      this.#pending = false;
      restoreControls();
    }
  }

  /**
   * Nuffies: tells the rest of the theme (cart count, drawer) that the free box went in.
   * @param {number | undefined} variantId
   */
  async #announceFreeTees(variantId) {
    if (!variantId) return;
    const root = window.Shopify?.routes?.root || '/';
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    try {
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'nuffies-freetees', itemCount: 1, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
    }
  }

  /**
   * Nuffies: FREETEES message under the code box (uses the code-error line, with its own wording).
   * @param {string} state - 'needs-towel' | 'unavailable' | 'failed'
   */
  #showFreeTeesNote(state) {
    const { cartDiscountError, cartDiscountErrorDiscountCode } = this.refs;
    if (!cartDiscountErrorDiscountCode.dataset.defaultText) {
      cartDiscountErrorDiscountCode.dataset.defaultText = cartDiscountErrorDiscountCode.textContent || '';
    }
    let text = 'FREETEES gives you a free box of The Local with any towel. Add a towel and your free tees go straight in.';
    if (state === 'unavailable') text = 'The Local is sold out right now, so FREETEES can’t be used yet.';
    if (state === 'failed') text = 'We couldn’t add your free tees. Please try FREETEES again.';
    cartDiscountErrorDiscountCode.textContent = text;
    cartDiscountError.classList.remove('hidden');
    cartDiscountErrorDiscountCode.classList.remove('hidden');
  }

  /**
   * Applies a discount to the cart.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#pending) return;

    if (this.#needsRefresh) {
      await this.#refreshStaleDiscounts();
      return;
    }

    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;
    const isFreeTees = discountCodeValue.toUpperCase() === FREE_TEES_CODE;

    const existingDiscounts = this.#existingDiscounts();
    const alreadyEntered = existingDiscounts.some((code) => code.toUpperCase() === discountCodeValue.toUpperCase());
    if (alreadyEntered && !isFreeTees) return;

    sectionRenderer.abortRender(this.dataset.sectionId);
    const abortController = this.#createAbortController();

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    if (cartDiscountErrorDiscountCode.dataset.defaultText) {
      cartDiscountErrorDiscountCode.textContent = cartDiscountErrorDiscountCode.dataset.defaultText;
    }

    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.#pending = true;
    const restoreControls = this.#disableControls();

    /** @type {{ state: string, variantId?: number }} */
    let freeTees = { state: '' };

    try {
      // Nuffies: put the free box in first, so the code applies straight away (see top).
      if (isFreeTees) freeTees = await addFreeTeesIfTowel(abortController.signal);

      // FREETEES already on the cart (e.g. entered before the towel): just re-draw with the box now in.
      if (alreadyEntered) {
        discountCode.value = '';
        deferredPromise.resolve({});
        await this.#refreshDiscountSection();
        if (freeTees.state === 'added') await this.#announceFreeTees(freeTees.variantId);
        else if (freeTees.state !== 'ready') this.#showFreeTeesNote(freeTees.state);
        return;
      }

      const allDiscountCodes = [...existingDiscounts, discountCodeValue];

      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: allDiscountCodes.map((code) => ({ code })),
          promise: deferredPromise.promise,
        })
      );

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      const sections = /** @type {Record<string, string> | null | undefined} */ (data.sections);
      const newHtml = sections?.[this.dataset.sectionId];
      const responseCodes = Array.isArray(data.discount_codes) ? data.discount_codes : [];
      const sameCode = (/** @type {{ code: string }} */ discount) =>
        String(discount.code).toUpperCase() === discountCodeValue.toUpperCase();

      // Nuffies: FREETEES kept on the cart but not applying yet (no towel, or the box couldn't be added):
      // re-draw the drawer and say what's needed, instead of the generic error.
      if (isFreeTees && responseCodes.find((discount) => sameCode(discount) && discount.applicable === false)) {
        discountCode.value = '';
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        if (newHtml) {
          sectionRenderer.abortRender(this.dataset.sectionId);
          await morphSection(this.dataset.sectionId, newHtml, {
            mode: this.closest('theme-drawer') ? 'hydration' : 'full',
          });
        } else {
          await this.#refreshDiscountSection();
        }
        this.#showFreeTeesNote(freeTees.state === 'added' || freeTees.state === 'ready' ? 'failed' : freeTees.state);
        return;
      }

      if (responseCodes.find((discount) => sameCode(discount) && discount.applicable === false)) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      /**
       * Liquid omits shipping discounts that the cart payload still reports as applicable.
       * @param {string} html - The rendered section HTML.
       * @returns {boolean} Whether the section can replace the current discount UI.
       */
      const shouldRender = (html) => {
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          if (
            codes.length === existingDiscounts.length &&
            codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
            responseCodes.find((discount) => sameCode(discount) && discount.applicable === true)
          ) {
            this.#handleDiscountError('shipping');
            return false;
          }
        }

        return true;
      };

      if (!newHtml) {
        discountCode.value = '';
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        await this.#refreshDiscountSection(shouldRender);
        if (freeTees.state === 'added') await this.#announceFreeTees(freeTees.variantId);
        return;
      }

      if (!shouldRender(newHtml)) {
        discountCode.value = '';
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      sectionRenderer.abortRender(this.dataset.sectionId);
      await morphSection(this.dataset.sectionId, newHtml, {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
      if (freeTees.state === 'added') await this.#announceFreeTees(freeTees.variantId);
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      this.#pending = false;
      restoreControls();
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#pending) return;

    if (this.#needsRefresh) {
      await this.#refreshStaleDiscounts();
      return;
    }

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    sectionRenderer.abortRender(this.dataset.sectionId);
    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.#pending = true;
    const restoreControls = this.#disableControls();

    try {
      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: existingDiscounts.map((code) => ({ code })),
          promise: deferredPromise.promise,
        })
      );

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const sections = /** @type {Record<string, string> | null | undefined} */ (data.sections);
      const newHtml = sections?.[this.dataset.sectionId];

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      if (!newHtml) {
        await this.#refreshDiscountSection();
        return;
      }

      sectionRenderer.abortRender(this.dataset.sectionId);
      await morphSection(this.dataset.sectionId, newHtml, {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      this.#pending = false;
      restoreControls();
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
